import { useNavigate, useSearch } from '@tanstack/react-router';
import { HTTPError } from 'ky';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { AuthService, type OAuthVendor } from '@/entities/auth/auth-service';
import { fetchUser } from '@/entities/auth/fetch-user';
import { useSignInWithGoogle } from '@/entities/auth/hooks/use-sign-in-with-google';
import { signInWithMicrosoft } from '@/entities/auth/sign-in-with-microsoft';
import { getLoginRedirectPath } from '@/entities/auth/utils/get-login-redirect-path';
import { trackLoginSuccess } from '@/shared/track/gtm';
import { extractUtmParams } from '@/shared/track/utm';
import { showDialog } from '@/shared/utils/show-dialog';
import { logger } from '@sentry/react';

const SSO_SIGN_IN_TIMEOUT_MS = 180_000;

export function useSocialSignIn() {
  const search = useSearch({ from: '/login/' });
  const token = search.token;
  const redirectPage = search.redirectPage;
  const utm = extractUtmParams(search);

  const [signInLoading, setSignInLoading] = useState(false);
  const navigate = useNavigate({ from: '/login' });
  const { signInWithGoogle } = useSignInWithGoogle({
    timeoutMs: SSO_SIGN_IN_TIMEOUT_MS,
  });

  const onSignInSuccess = useCallback(async () => {
    try {
      const redirectPath = await getLoginRedirectPath(token, redirectPage);
      await navigate(redirectPath);
    } catch (error) {
      logger.error('Post-login redirect failed', {
        extra: {
          redirectPage,
          token,
        },
      });
      toast.error('Failed to login');
      throw error;
    }
  }, [navigate, redirectPage, token]);

  const handleSocialLoginError = useCallback(async (vendor: OAuthVendor, error: unknown) => {
    if (error instanceof HTTPError) {
      const errorBody: {
        code: number;
        data: null;
        message: string;
      } = await error.response.json();
      if (errorBody.code === 101) {
        showDialog({
          title: 'Please use your work email',
          message:
            'Personal email addresses are not allowed for registration. Please sign up with your work email (e.g., user@yourcompany.com).',
          confirmLabel: 'Confirm',
          hideCancel: true,
        });
        return;
      }

      logger.error(`${vendor} sign-in API error`, {
        extra: errorBody,
      });
    } else if (error instanceof Error) {
      logger.error(`${vendor} sign-in unexpected error`, {
        message: error.message,
        name: error.name,
        stack: error.stack,
      });
    }

    showDialog({
      title: 'Login failed',
      message: 'An unknown error occurred. Please contact support if the problem persists.',
      confirmLabel: 'Confirm',
      hideCancel: true,
    });
  }, []);

  const handleSignin = useCallback(
    async (signInReqest: () => Promise<string>, vendor: OAuthVendor) => {
      try {
        setSignInLoading(true);
        const oauthToken = await signInReqest();
        await AuthService.login(oauthToken, vendor, utm);
        const user = await fetchUser();
        trackLoginSuccess(user.email, vendor, utm);
        await onSignInSuccess();
        setSignInLoading(false);
      } catch (error) {
        await handleSocialLoginError(vendor, error);
        setSignInLoading(false);
      }
    },
    [handleSocialLoginError, onSignInSuccess, utm],
  );

  const onSignInWithGoogleClick = useCallback(() => {
    handleSignin(signInWithGoogle, 'google');
  }, [handleSignin, signInWithGoogle]);

  const onSignInWithMicrosoftClick = useCallback(() => {
    handleSignin(() => signInWithMicrosoft({ timeoutMs: SSO_SIGN_IN_TIMEOUT_MS }), 'microsoft');
  }, [handleSignin]);

  return {
    signInLoading,
    onSignInWithGoogleClick,
    onSignInWithMicrosoftClick,
  };
}
